// The OBS remote: cutting scenes from a console on another device, through
// the console tab on the streaming PC.
//
// obsControl reaches OBS from the browser, and a browser allows a plain
// ws:// connection from an https page to the same machine only (see the top
// of obsControl.js): an iPad on the LAN can't connect to OBS at all. So the
// tab on the PC with OBS control on is the RELAY: it polls the server's
// obs_remote row (migration 0058, functions/api/packages/[slug]/obs-remote.js)
// with its heartbeat and runs the cut a remote wrote there, against OBS,
// through obsControl as the tiles do. Any other console of the same account
// and package is a REMOTE: its OBS Control tab polls the same row, shows the
// relay's state (the scene on air) and writes a cut. Both loops live here;
// which one a tab runs follows obsControl: the relay loop whenever OBS
// control is on in this tab (page-level, so the OBS Control tab needn't be
// open on the PC), the remote loop while the card is open here and this tab
// isn't the one connected. A tab with control on that never connects (an
// iPhone, which can't: iOS blocks ws:// from an https page even to
// localhost) runs both and cuts through the PC like any remote. Each tab
// carries a random id in its heartbeat (TAB_ID), so a tab never takes its
// own heartbeat for the PC's (a PC whose OBS just closed), and the server
// lets a tab overwrite only its own live connected heartbeat.
//
// The relay's poll is ticked by a Web Worker: Chrome slows a hidden tab's
// timers to once a minute after five minutes in the background (the PC's
// console is behind OBS all show long), and a worker's timer isn't
// throttled. The worker only ticks; the request and the OBS command run on
// the page. It polls every second while a remote is looking (the server
// says) and every 8 s otherwise.
//
// Pairing: OBS's Connect Info dialog shows a QR code of its address, port
// and password (obsws://host:port/password). A device with a camera (the
// iPad, QrScanner.jsx) scans it and leaves it on the row; the relay takes
// it on its next poll, connects to ws://127.0.0.1:port with the password
// (the only address a tab can use, whatever IP OBS guessed), and tells the
// server, which clears it. The password passes through the server, which is
// why a pairing is dropped after ten minutes unclaimed; it is kept only in
// the PC's localStorage, as it always was. A scan on the PC's own tab (its
// webcam) applies itself at once as well.
//
// Beyond cuts, a remote's panels work the same way (the club's ask,
// 2026-10-28: the deck as the whole stream control): the relay's heartbeat
// carries what its own panels show (the mixer's inputs with mute and
// volume, the In-Match sides, the automation's settings, the cameras'; no
// level meters, which change fifteen times a second) and a remote sends
// actions (mute, volume, swap, cutSetting, camerasFollow, layout) through
// the same queue as a cut, one in flight at a time. The commentator layout
// is the console's to write (its state, which it autosaves), so the console
// open on the PC lends the relay its switch (setLayoutHandler); the deck
// page alone refuses it.
//
// Commands never throw; what happens goes in obsControl's activity log on
// both ends.

import { api } from '../api/client.js';
import { obsControl } from './obsControl.js';
import { obsStage } from './obsStage.js';

export const RELAY_IDLE_MS = 8000;
export const RELAY_ACTIVE_MS = 1000;
const REMOTE_MS = 2000;
const REMOTE_PENDING_MS = 700;
// A cut the relay hasn't answered by then is reported as unanswered: it may
// still run if the relay was only slow, which the tiles then show.
const PENDING_TIMEOUT_MS = 10000;
const RELAY_HOST = 'ws://127.0.0.1';
const DEFAULT_PORT = 4455;
// This page load's name in its heartbeats (see the top of the file).
const TAB_ID = typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
// The pairing this PC last applied (its `at`), so a refresh doesn't apply
// it again from a response that crossed the "taken" acknowledgement.
const PAIRED_STORAGE_KEY = 'streamgg.obs.paired';

const TICKER_SOURCE = 'let t=null;onmessage=(e)=>{clearInterval(t);t=null;if(e.data>0)t=setInterval(()=>postMessage(1),e.data);};';
// OBS's own event with an input's new mute or volume lands a moment after
// the request: the answer's heartbeat waits for it, so a remote sees the
// change with the answer.
const INPUT_EVENT_MS = 250;

// A command as the log names it.
function describe(c) {
  switch (c.action) {
    case 'cut': return `cut "${c.key}" → "${obsControl.sceneMap[c.key] ?? c.key}"`;
    case 'mute': return `${c.muted ? 'mute' : 'unmute'} "${c.input}"`;
    case 'volume': return `volume "${c.input}" ${c.db <= -100 ? '−∞' : Number(c.db).toFixed(1)} dB`;
    case 'swap': return typeof c.swapped === 'boolean' ? `In-Match sides ${c.swapped ? 'swapped' : 'as start.gg has them'}` : 'swap the In-Match sides';
    case 'cutSetting': return `${c.which === 'brb' ? 'Be Right Back' : 'Starting Soon'} cut ${typeof c.on === 'boolean' ? (c.on ? 'on' : 'off') : `→ "${c.to}"`}`;
    case 'camerasFollow': return `cameras follow the layout: ${c.on ? 'on' : 'off'}`;
    case 'layout': return `commentator layout: ${c.shared ? 'camera only' : 'camera + gameplay'}`;
    default: return String(c.action);
  }
}

const readJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? null;
  } catch {
    return null;
  }
};
const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full / disabled */
  }
};

// OBS's Connect Info as its QR code (and a pasted copy) carries it:
// obsws://192.168.1.20:4455/password, the password percent-encoded and
// everything after the first slash, absent with authentication off. The
// host is whatever address OBS guessed for the LAN, an IPv6 one written
// WITHOUT brackets (obsws://fdd8:a491:…:ee2f:9098/password; the club's PC,
// 2026-10-28), so the port is the last :digits before the slash, and the
// host is only shown (the relay connects to the loopback address). Null
// when the text isn't one.
export function parseObsws(text) {
  const m = /^(?:obsws|wss?):\/\/(.+?)(?::(\d{1,5}))?(?:\/(.*))?$/i.exec(String(text ?? '').trim());
  if (!m) return null;
  const port = m[2] ? Number(m[2]) : DEFAULT_PORT;
  if (!(port >= 1 && port <= 65535)) return null;
  const host = m[1].replace(/^\[(.*)\]$/, '$1');
  if (!host || /[\s/?#]/.test(host)) return null;
  let password = m[3] ?? '';
  try {
    password = decodeURIComponent(password);
  } catch {
    // Not percent-encoded after all: as it is.
  }
  return { host, port, password };
}

// A host as an address reads: an IPv6 one in brackets.
export const showHost = (host) => (host.includes(':') ? `[${host}]` : host);

// The address a tab on the OBS PC connects to for a pairing: always the
// loopback one, whatever address OBS guessed for the LAN.
export const pairedUrl = (port) => `${RELAY_HOST}:${port}`;

// A ticker in a worker (see the top of the file); plain timers where there
// are no workers.
function makeTicker(onTick) {
  if (typeof Worker !== 'undefined' && typeof Blob !== 'undefined' && typeof URL?.createObjectURL === 'function') {
    try {
      const url = URL.createObjectURL(new Blob([TICKER_SOURCE], { type: 'text/javascript' }));
      const worker = new Worker(url);
      worker.onmessage = onTick;
      return {
        set: (ms) => worker.postMessage(ms),
        stop: () => {
          worker.terminate();
          URL.revokeObjectURL(url);
        },
      };
    } catch {
      // Blob workers refused (a CSP): timers, then.
    }
  }
  let timer = null;
  return {
    set: (ms) => {
      clearInterval(timer);
      timer = ms > 0 ? setInterval(onTick, ms) : null;
    },
    stop: () => clearInterval(timer),
  };
}

class ObsRemote {
  constructor() {
    this.packageSlug = null;
    // The relay as the server last described it to this remote:
    // { alive, status, scene, key, studio, interval, at }, null before the
    // first answer or when no PC tab ever reported.
    this.relay = null;
    // A command sent from here, until the relay answers: the command with
    // its at, label and sentAt; the next ones wait in _queue.
    this.pending = null;
    this._queue = [];
    this._sending = false;
    this._layoutHandler = null;
    // The last scan handed over from here: { host, port, at, pending, taken }.
    this.pairing = null;
    this.relayOn = false; // this tab polls as the relay
    this.relayError = null; // the relay poll's last failure, null while fine
    this.remoteError = null;

    this._ticker = null;
    this._relayInterval = 0;
    this._relayBusy = false;
    this._relayAgain = false;
    this._relayFailures = 0;
    this._lastStatus = null;
    this._lastCommandAt = 0;
    this._done = null; // the relay's answer to the last cut it ran
    this._pairingTaken = null;

    this._watching = false;
    this._remoteTimer = null;
    this._remoteBusy = false;
    this._remoteSeq = 0;

    this._listeners = new Set();
    this._snap = null;
    this._rebuildSnapshot();
    this.subscribe = this.subscribe.bind(this);
    this.getSnapshot = this.getSnapshot.bind(this);

    obsControl.subscribe(() => {
      this._syncRelay();
      this._syncRemote();
    });
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => this._syncRemote());
    }
  }

  // ── subscription (useSyncExternalStore contract) ─────────────────────────

  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  getSnapshot() {
    return this._snap;
  }

  _rebuildSnapshot() {
    this._snap = {
      relay: this.relay,
      pending: this.pending,
      pairing: this.pairing,
      relayOn: this.relayOn,
      relayError: this.relayError,
      remoteError: this.remoteError,
    };
  }

  _emit() {
    this._rebuildSnapshot();
    for (const listener of this._listeners) listener();
  }

  // ── wiring ───────────────────────────────────────────────────────────────

  // Page-level, with the package: the relay loop runs for it whenever OBS
  // control is on in this tab.
  configure({ packageSlug }) {
    if (packageSlug === this.packageSlug) return;
    this._stopRelay();
    this._stopRemote();
    this.packageSlug = packageSlug ?? null;
    this.relay = null;
    this.pending = null;
    for (const q of this._queue) q.resolve(false);
    this._queue = [];
    this.pairing = null;
    this._lastCommandAt = 0;
    this._done = null;
    this._pairingTaken = null;
    this._syncRelay();
    this._syncRemote();
    this._emit();
  }

  // The console on the OBS PC lends the relay its commentator layout switch
  // (PackageProduction's setCommentatorLayout, which writes the state the
  // console autosaves); without one (the deck page) a layout command is
  // refused.
  setLayoutHandler(fn) {
    this._layoutHandler = typeof fn === 'function' ? fn : null;
  }

  // The card, while it is open: this tab looks at the relay (as a remote)
  // unless it is the one connected to OBS.
  watch(on) {
    this._watching = on === true;
    this._syncRemote();
  }

  // Whether cuts from this tab should go through the relay: another tab's,
  // alive and connected to OBS, while this one isn't connected.
  get viaRelay() {
    return this._wantRemote() && this.relay?.alive === true && this.relay.status === 'connected' && this.relay.mine !== true;
  }

  // The remote loop runs while the card is open in a tab that isn't the one
  // connected to OBS, in the foreground.
  _wantRemote() {
    return !!this.packageSlug && this._watching && !obsControl.isConnected
      && (typeof document === 'undefined' || document.visibilityState !== 'hidden');
  }

  // ── the relay (the tab on the OBS PC) ────────────────────────────────────

  _syncRelay() {
    const want = !!this.packageSlug && obsControl.connection.enabled === true;
    if (want && !this._ticker) {
      this._ticker = makeTicker(() => this._relayTick());
      this._relayInterval = RELAY_IDLE_MS;
      this._ticker.set(this._relayInterval);
      this.relayOn = true;
      this._relayFailures = 0;
      this._emit();
      this._relayTick();
    } else if (!want && this._ticker) {
      this._stopRelay();
      this._emit();
    }
    // A change of OBS status is told at once, not on the next tick.
    if (this._ticker && obsControl.status !== this._lastStatus) {
      this._lastStatus = obsControl.status;
      this._relayTick();
    }
  }

  _stopRelay() {
    if (this._ticker) this._ticker.stop();
    this._ticker = null;
    this.relayOn = false;
    this.relayError = null;
    this._lastStatus = null;
  }

  _heartbeat() {
    const scene = obsControl.currentProgramScene;
    const key = scene ? (Object.entries(obsControl.sceneMap).find(([, name]) => name === scene)?.[0] ?? null) : null;
    return {
      tab: TAB_ID,
      status: obsControl.status,
      scene,
      key,
      studio: obsControl.studioMode === true,
      interval: this._relayInterval,
      stage: this._stageSnapshot(),
    };
  }

  // What this tab's panels show, for a remote's (see the top of the file).
  _stageSnapshot() {
    const mic = obsControl.mic;
    return {
      audio: obsControl.audio.map(({ name, role, muted, volumeDb }) => ({
        name, role, muted: muted === true, volumeDb: Number.isFinite(volumeDb) ? volumeDb : null,
      })),
      mic: mic ? { input: mic.input, model: mic.model, device: mic.device, matched: mic.matched === true } : null,
      swapped: obsStage.swapped,
      cuts: obsStage.settings.cuts,
      camerasFollow: obsStage.settings.camerasFollow === true,
      layout: obsStage.layout,
      applied: obsStage.applied,
      console: typeof this._layoutHandler === 'function',
    };
  }

  async _relayTick() {
    if (!this._ticker || !this.packageSlug) return;
    if (this._relayBusy) {
      this._relayAgain = true;
      return;
    }
    this._relayBusy = true;
    const slug = this.packageSlug;
    try {
      const body = { role: 'relay', relay: this._heartbeat() };
      if (this._done) body.done = this._done;
      if (this._pairingTaken != null) body.pairingTaken = this._pairingTaken;
      const res = await api.packageObsRemote(slug, body);
      if (slug !== this.packageSlug || !this._ticker) return;
      this._relayFailures = 0;
      if (this.relayError) {
        this.relayError = null;
        this._emit();
      }
      if (this._pairingTaken != null && !res.pairing) this._pairingTaken = null;
      // An answer the server has (it no longer hands that cut out) needn't
      // be sent again.
      if (this._done && Number(res.command?.at) !== this._done.at) this._done = null;
      const interval = res.remoteActive ? RELAY_ACTIVE_MS : RELAY_IDLE_MS;
      if (interval !== this._relayInterval) {
        this._relayInterval = interval;
        this._ticker.set(interval);
      }
      if (res.pairing && Number(res.pairing.at) !== readJson(PAIRED_STORAGE_KEY)) this._takePairing(res.pairing);
      if (res.command && Number(res.command.at) > this._lastCommandAt) {
        this._lastCommandAt = Number(res.command.at);
        await this._runCommand(res.command);
        this._relayAgain = true; // the answer goes back now, not a tick later
      }
    } catch (e) {
      this._relayFailures += 1;
      if (this._relayFailures === 3) {
        obsControl.note(false, `OBS remote: this tab can't report to the server — ${e.message}`);
      }
      if (this.relayError !== e.message) {
        this.relayError = e.message;
        this._emit();
      }
    } finally {
      this._relayBusy = false;
      if (this._relayAgain) {
        this._relayAgain = false;
        this._relayTick();
      }
    }
  }

  async _runCommand(command) {
    // { at, key } alone is a cut from before actions (a row written just
    // before a deploy).
    const { at, action = command.key ? 'cut' : undefined } = command;
    const label = describe({ ...command, action });
    if (action !== 'volume') obsControl.note(true, `remote: ${label}, asked from another device`);
    let ok = false;
    let why = null;
    try {
      switch (action) {
        case 'cut':
          ok = obsControl.studioMode
            ? await obsControl.previewAndTake(command.key)
            : await obsControl.setScene(command.key);
          break;
        case 'mute':
          ok = await obsControl.setInputMuted(command.input, command.muted === true);
          break;
        case 'volume':
          ok = await obsControl.setInputVolumeDb(command.input, command.db);
          break;
        case 'swap':
          if (obsStage.swapped === null) why = 'In-Match has no swapped twin in OBS';
          else ok = await obsStage.swapSides(typeof command.swapped === 'boolean' ? command.swapped : undefined);
          break;
        case 'cutSetting': {
          const patch = {};
          if (typeof command.on === 'boolean') patch.on = command.on;
          if (typeof command.to === 'string') patch.to = command.to;
          obsStage.setCut(command.which, patch);
          ok = true;
          break;
        }
        case 'camerasFollow':
          obsStage.setCamerasFollow(command.on === true);
          ok = true;
          break;
        case 'layout':
          if (this._layoutHandler) {
            this._layoutHandler(command.shared === true);
            ok = true;
          } else {
            why = "the console isn't open on the OBS PC (the deck alone can't switch the layout)";
          }
          break;
        default:
          why = `unknown action "${action}"`;
      }
    } catch (e) {
      why = e.message;
    }
    if (!ok && why) obsControl.note(false, `remote: ${label}: FAILED — ${why}`);
    // Otherwise the reason is the line the action just logged (obsControl's
    // own list: its snapshot is rebuilt only on an emit, which a failed
    // switch skips).
    if (!ok && !why) why = obsControl.logEntries[0]?.text ?? 'failed';
    if (ok && (action === 'mute' || action === 'volume')) await new Promise((r) => setTimeout(r, INPUT_EVENT_MS));
    this._done = { at: Number(at), ok, error: ok ? null : why };
  }

  _takePairing(pairing) {
    const { host, port, password, at } = pairing;
    writeJson(PAIRED_STORAGE_KEY, Number(at));
    this._pairingTaken = Number(at);
    obsControl.note(true, `Paired from a scan: OBS at ${showHost(host)}:${port} — connecting to it on this PC`);
    obsControl.saveConnection({ url: pairedUrl(port), password: password ?? '', enabled: true });
    this._relayAgain = true;
  }

  // ── a remote (any other console) ─────────────────────────────────────────

  _syncRemote() {
    const want = this._wantRemote();
    if (want && !this._remoteTimer && !this._remoteBusy) this._remoteTick();
    else if (!want) this._stopRemote();
  }

  _stopRemote() {
    clearTimeout(this._remoteTimer);
    this._remoteTimer = null;
    this._remoteSeq += 1;
  }

  _scheduleRemote(delay) {
    clearTimeout(this._remoteTimer);
    const seq = this._remoteSeq;
    this._remoteTimer = setTimeout(() => {
      this._remoteTimer = null;
      if (seq === this._remoteSeq) this._remoteTick();
    }, delay);
  }

  async _remoteTick() {
    if (this._remoteBusy) return;
    const slug = this.packageSlug;
    const seq = this._remoteSeq;
    this._remoteBusy = true;
    try {
      const res = await api.packageObsRemote(slug, { role: 'remote' });
      if (slug !== this.packageSlug || seq !== this._remoteSeq) return;
      this.remoteError = null;
      this._applyRemote(res);
    } catch (e) {
      if (slug !== this.packageSlug || seq !== this._remoteSeq) return;
      this.remoteError = e.message;
      this._emit();
    } finally {
      this._remoteBusy = false;
    }
    if (this._wantRemote() && seq === this._remoteSeq) this._scheduleRemote(this.pending ? REMOTE_PENDING_MS : REMOTE_MS);
  }

  _applyRemote(res) {
    // `mine`: this very tab's heartbeat (its OBS closed), no relay for it.
    this.relay = res.relay ? { ...res.relay, mine: res.relay.tab === TAB_ID } : null;
    if (this.pending) {
      const { at, action, label, sentAt } = this.pending;
      if (res.done && Number(res.done.at) === at) {
        // A fader drag's many volumes aren't worth a line each.
        if (action !== 'volume' || !res.done.ok) {
          obsControl.note(res.done.ok, `${label} on the OBS PC: ${res.done.ok ? 'OK' : `FAILED — ${res.done.error ?? 'see its activity log'}`}`);
        }
        this.pending = null;
      } else if (Date.now() - sentAt > PENDING_TIMEOUT_MS) {
        obsControl.note(false, `${label}: no answer from the OBS PC in ${PENDING_TIMEOUT_MS / 1000} s`);
        this.pending = null;
      }
      if (!this.pending) this._pump();
    }
    if (this.pairing && this.pairing.pending) {
      const stillWaiting = res.pairingPending && Number(res.pairingAt) === this.pairing.at;
      if (!stillWaiting) {
        // Cleared by the relay taking it (the usual), or dropped after its
        // ten minutes: the relay's state says which.
        const taken = this.relay?.alive === true;
        this.pairing = { ...this.pairing, pending: false, taken };
        obsControl.note(taken, taken
          ? `The OBS PC took the scan: connecting to OBS at ${showHost(this.pairing.host)}:${this.pairing.port} there`
          : 'The scan was not taken: open this console on the OBS PC with OBS control on, then scan again');
      }
    }
    this._emit();
  }

  // A cut from this device, run by the relay.
  cut(key) {
    return this.send({ action: 'cut', key });
  }

  // A command from this device for the relay to run, queued behind the one
  // in flight, one at a time; a volume for an input replaces one still
  // queued for it (a fader drag). Resolves true once sent, false (logged)
  // when it can't be.
  send(command) {
    if (!this.packageSlug) return Promise.resolve(false);
    if (command.action === 'volume') {
      this._queue = this._queue.filter((c) => !(c.action === 'volume' && c.input === command.input));
    }
    return new Promise((resolve) => {
      this._queue.push({ command, resolve });
      this._pump();
    });
  }

  async _pump() {
    if (this.pending || this._sending || !this._queue.length || !this.packageSlug) return;
    const { command, resolve } = this._queue.shift();
    const label = describe(command);
    this._sending = true;
    try {
      const res = await api.packageObsRemote(this.packageSlug, { role: 'remote', command });
      this.pending = { ...command, at: Number(res.command.at), label, sentAt: Date.now() };
      if (command.action !== 'volume') obsControl.note(true, `${label}: sent to the OBS PC`);
      this._applyRemote(res);
      this._syncRemote();
      if (this._remoteTimer) this._scheduleRemote(REMOTE_PENDING_MS);
      resolve(true);
    } catch (e) {
      obsControl.note(false, `${label}: FAILED — ${e.message}`);
      this._emit();
      resolve(false);
    } finally {
      this._sending = false;
    }
    if (!this.pending) this._pump();
  }

  // OBS's Connect Info, scanned here: left for the relay, and applied to
  // this tab too when it is the one with OBS control on.
  async pair({ host, port, password }) {
    if (!this.packageSlug) return false;
    try {
      const res = await api.packageObsRemote(this.packageSlug, { role: 'remote', pairing: { host, port, password } });
      this.pairing = { host, port, at: Number(res.pairingAt), pending: true, taken: false };
      obsControl.note(true, `Scanned OBS's Connect Info: ${showHost(host)}:${port} — handed to the console on the OBS PC`);
      if (obsControl.connection.enabled) {
        writeJson(PAIRED_STORAGE_KEY, Number(res.pairingAt));
        this._pairingTaken = Number(res.pairingAt);
        obsControl.saveConnection({ url: pairedUrl(port), password, enabled: true });
      }
      this._applyRemote(res);
      this._syncRemote();
      return true;
    } catch (e) {
      obsControl.note(false, `Scan: FAILED — ${e.message}`);
      this._emit();
      return false;
    }
  }
}

export const obsRemote = new ObsRemote();
